import { createFileRoute, Link } from "@tanstack/react-router";
import { Shield, Lock, EyeOff, Heart, UserCheck, BellOff, MessageSquareOff, Users } from "lucide-react";
import { useLang } from "@/hooks/use-language";

export const Route = createFileRoute("/safety")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://jozori.com/safety" },
      { title: "Child Safety & COPPA — Jozori | سلامة الأطفال — جذوري" },
      {
        name: "description",
        content:
          "How Jozori keeps children safe: no ads, no tracking, no chat, no data sales. كيف تحافظ جذوري على سلامة الأطفال: بدون إعلانات أو تتبّع أو محادثات.",
      },
      { property: "og:title", content: "Child Safety at Jozori | سلامة الأطفال في جذوري" },
      {
        property: "og:description",
        content:
          "Designed for children. Trusted by parents. مُصمَّمة للأطفال، يثق بها الآباء.",
      },
    ],
    links: [{ rel: "canonical", href: "https://jozori.com/safety" }],
  }),
  component: SafetyPage,
});

type Copy = {
  badge: string;
  hero1: string;
  hero2: string;
  intro: string;
  pillarsHeading: string;
  pillars: { Icon: typeof Shield; title: string; body: string }[];
  regHeading: string;
  regIntro: string;
  regBullets: string[];
  neverHeading: string;
  neverHighlight: string;
  neverBullets: string[];
  contactHeading: string;
  contactBody: string;
  privacy: string;
  terms: string;
  seeAlso: string;
  home: string;
  toggle: string;
};

const COPY: Record<"en" | "ar", Copy> = {
  en: {
    badge: "Child Safety",
    hero1: "Designed for children.",
    hero2: "Trusted by parents.",
    intro:
      "Jozori is built from the ground up around a single rule: your child is not the product. No ads, no chat, no third-party tracking. Just stories and learning.",
    pillarsHeading: "Safety pillars",
    pillars: [
      { Icon: EyeOff, title: "Zero advertising — ever", body: "Children never see ads, sponsored content, or upsell prompts. No third-party advertising SDKs are shipped in the app." },
      { Icon: MessageSquareOff, title: "No chat, no strangers", body: "Children cannot message anyone, post content publicly, or be contacted by other users. The world inside Jozori is closed and curated." },
      { Icon: Lock, title: "Parent-gated everything", body: "Account creation, billing, settings, and external links are protected by a parent PIN gate that uses age-verifiable challenges, not just numbers." },
      { Icon: BellOff, title: "No behavioural tracking", body: "We don't sell data, we don't share with marketers, and we don't build advertising profiles of your children. Period." },
      { Icon: UserCheck, title: "Parents create profiles", body: "Only a verified parent or legal guardian can create an account. Child profiles live inside the parent account — children never sign up directly." },
      { Icon: Users, title: "Human-reviewed content", body: "Every story, video, and challenge is reviewed by Arabic-speaking educators and parents before it ever reaches a child profile." },
    ],
    regHeading: "Aligned with COPPA & GDPR-K",
    regIntro:
      "Jozori is designed to comply with the spirit of the U.S. Children's Online Privacy Protection Act (COPPA) and the EU's GDPR provisions for children (GDPR-K). In practice that means:",
    regBullets: [
      "Verifiable parental consent is required before any child profile is created.",
      "We collect the minimum data necessary to operate the service: a child's first name or nickname, age tier, avatar, and learning progress. No location, no contacts, no biometrics, no photos from the device.",
      "We do not sell, rent, or share children's data with marketers, data brokers, or advertising networks.",
      "Parents can view, export, or permanently delete their family's data at any time from Settings → Privacy.",
      "All data is stored on infrastructure governed by formal data-processing agreements with appropriate technical and organisational security controls (encryption at rest and in transit, access controls, audit logging).",
    ],
    neverHeading: "What Jozori will",
    neverHighlight: "never",
    neverBullets: [
      "· Show your child a third-party advertisement.",
      "· Allow strangers to message, friend, or contact your child.",
      "· Sell, rent, or trade your family's data — to anyone, for any reason.",
      "· Use dark patterns, manipulative loot boxes, or fake scarcity to drive purchases.",
      "· Push notifications to children's profiles. Only parents can opt into reminders.",
    ],
    contactHeading: "Questions about your family's safety?",
    contactBody:
      "Our team — many of us are parents ourselves — replies personally within one business day.",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    seeAlso: "See also our",
    home: "← Home",
    toggle: "AR",
  },
  ar: {
    badge: "سلامة الأطفال",
    hero1: "مُصمَّمة للأطفال.",
    hero2: "يثق بها الآباء.",
    intro:
      "بُنيت جذوري منذ البداية على قاعدة واحدة: طفلك ليس سلعة. بدون إعلانات، بدون محادثات، بدون تتبّع من أطراف ثالثة. فقط قصص وتعلّم.",
    pillarsHeading: "ركائز السلامة",
    pillars: [
      { Icon: EyeOff, title: "صفر إعلانات — على الإطلاق", body: "لا يرى الأطفال أبدًا أي إعلانات أو محتوى مدفوع أو نوافذ ترويجية. لا تشتمل تطبيقاتنا على أي SDKs للإعلانات." },
      { Icon: MessageSquareOff, title: "بدون محادثات أو غرباء", body: "لا يمكن للأطفال مراسلة أحد أو نشر محتوى علني أو تلقّي رسائل من مستخدمين آخرين. عالم جذوري مغلق ومُنتقى بعناية." },
      { Icon: Lock, title: "كل شيء محمي بقفل الوالد", body: "إنشاء الحساب والفوترة والإعدادات والروابط الخارجية محمية بقفل PIN للوالد يعتمد على تحدّيات يتحقّق فيها العمر، وليس مجرد أرقام." },
      { Icon: BellOff, title: "بدون تتبّع سلوكي", body: "نحن لا نبيع البيانات ولا نشاركها مع المسوّقين ولا نبني أي ملفّات إعلانية لأطفالك. نقطة." },
      { Icon: UserCheck, title: "الوالد هو من ينشئ الملفات", body: "لا يحقّ إلا لوالدٍ أو وليّ قانوني مُتحقَّق منه إنشاء حساب. ملفات الأطفال تعيش داخل حساب الوالد، ولا يسجّل الطفل بنفسه أبدًا." },
      { Icon: Users, title: "محتوى يراجعه إنسان", body: "تخضع كل قصة وفيديو وتحدٍّ لمراجعة مربّين وآباء يتحدّثون العربية قبل أن يصل إلى أي ملف طفل." },
    ],
    regHeading: "متوافقون مع COPPA و GDPR-K",
    regIntro:
      "صُمّمت جذوري لتتوافق مع روح قانون حماية خصوصية الأطفال على الإنترنت COPPA الأمريكي، وأحكام اللائحة الأوروبية لحماية بيانات الأطفال GDPR-K. عمليًا، يعني هذا:",
    regBullets: [
      "موافقة الوالد المُتحقَّق منها مطلوبة قبل إنشاء أي ملف طفل.",
      "نجمع الحد الأدنى من البيانات اللازمة لتشغيل الخدمة: الاسم الأول أو الكنية، الفئة العمرية، الصورة الرمزية، ونمو الطفل وتقدّمه. لا موقع، لا جهات اتصال، لا بيانات بيومترية، لا صور من الجهاز.",
      "لا نبيع بيانات الأطفال ولا نؤجّرها ولا نشاركها مع المسوّقين أو وسطاء البيانات أو شبكات الإعلانات.",
      "يمكن للوالدَين عرض بيانات العائلة أو تصديرها أو حذفها نهائيًا في أي وقت من الإعدادات ← الخصوصية.",
      "تُخزَّن جميع البيانات على بنية تحتية محكومة باتفاقيات معالجة بيانات رسمية، مع ضوابط أمنية فنية وتنظيمية مناسبة (تشفير في حالة السكون وأثناء النقل، ضوابط وصول، وسجلات تدقيق).",
    ],
    neverHeading: "ما لن تفعله جذوري",
    neverHighlight: "أبدًا",
    neverBullets: [
      "· عرض إعلان طرف ثالث على طفلك.",
      "· السماح للغرباء بمراسلة طفلك أو إضافته أو التواصل معه.",
      "· بيع بيانات عائلتك أو تأجيرها أو تداولها — لأي جهة، ولأي سبب.",
      "· استخدام أنماط مضلّلة أو صناديق غنائم أو ندرة مزيّفة لدفع المشتريات.",
      "· إرسال إشعارات إلى ملفات الأطفال. التذكيرات متاحة فقط للوالدَين بعد موافقتهما.",
    ],
    contactHeading: "أسئلة حول سلامة عائلتك؟",
    contactBody:
      "فريقنا — وكثير منا آباء وأمهات أنفسنا — يردّ شخصيًا خلال يوم عمل واحد.",
    privacy: "سياسة الخصوصية",
    terms: "الشروط والأحكام",
    seeAlso: "راجع أيضًا",
    home: "← الرئيسية",
    toggle: "EN",
  },
};

function SafetyPage() {
  const { lang, dir, toggle } = useLang();
  const c = COPY[lang];
  const isAr = lang === "ar";

  return (
    <div dir={dir} className="min-h-dvh bg-background text-foreground">
      <header className="px-5 py-5 border-b border-clay/30">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-display-ar text-2xl font-black text-primary">جذوري</span>
            <span className="font-display-en text-lg font-extrabold text-foreground/40">/</span>
            <span className="font-display-en text-lg font-extrabold tracking-tight">JOZORI</span>
          </Link>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggle}
              className="rounded-full border border-clay/50 bg-card px-3 py-1.5 text-xs font-bold tracking-widest text-foreground/70 hover:text-primary"
            >
              {c.toggle}
            </button>
            <Link to="/" className="text-sm font-semibold text-foreground/60 hover:text-primary">
              {c.home}
            </Link>
          </div>
        </div>
      </header>

      <main style={isAr ? { fontFamily: "'Cairo', system-ui, sans-serif" } : undefined}>
        {/* Hero */}
        <section className="px-5 pt-16 pb-12">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-bold uppercase tracking-widest mb-6">
              <Shield className="size-3" aria-hidden="true" />
              {c.badge}
            </span>
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-5 leading-tight">
              {c.hero1}
              <br />
              <span className="text-primary">{c.hero2}</span>
            </h1>
            <p className="text-lg md:text-xl text-foreground/70 max-w-2xl mx-auto">{c.intro}</p>
          </div>
        </section>

        {/* Pillars */}
        <section aria-label={c.pillarsHeading} className="px-5 pb-16">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-5">
            {c.pillars.map(({ Icon, title, body }) => (
              <div
                key={title}
                className="bg-card border border-clay/40 rounded-3xl p-7 hover:border-primary/30 hover:shadow-xl transition-all"
              >
                <div className="size-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-5">
                  <Icon className="size-5" aria-hidden="true" />
                </div>
                <h2 className="text-lg font-extrabold mb-2 tracking-tight">{title}</h2>
                <p className="text-sm text-foreground/65 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* COPPA / GDPR-K commitments */}
        <section aria-label={c.regHeading} className="px-5 py-16 bg-clay/15">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-6">{c.regHeading}</h2>
            <p className="text-foreground/75 leading-relaxed mb-4">{c.regIntro}</p>
            <ul className="space-y-3 text-foreground/75 leading-relaxed list-disc ps-6">
              {c.regBullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* What we never do */}
        <section className="px-5 py-16">
          <div className="max-w-3xl mx-auto bg-foreground text-background rounded-[2rem] p-10 md:p-14">
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-6">
              {c.neverHeading} <span className="text-accent">{c.neverHighlight}</span>
            </h2>
            <ul className="space-y-3 text-base md:text-lg opacity-90">
              {c.neverBullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* Contact */}
        <section className="px-5 py-16 bg-clay/15 text-center">
          <div className="max-w-2xl mx-auto">
            <Heart className="size-8 text-primary mx-auto mb-4" aria-hidden="true" />
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight mb-3">{c.contactHeading}</h2>
            <p className="text-foreground/65 mb-6">{c.contactBody}</p>
            <a
              href="mailto:safety@jozori.com"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-2xl font-bold shadow-press active:translate-y-0.5 active:shadow-none transition-all"
            >
              safety@jozori.com
            </a>
            <p className="mt-6 text-xs text-foreground/40">
              {c.seeAlso}{" "}
              <Link to="/privacy" className="underline hover:text-primary">{c.privacy}</Link>
              {isAr ? " و " : " and "}
              <Link to="/terms" className="underline hover:text-primary">{c.terms}</Link>.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}
